/**
 * THE ONE FOCUS RULE (P36)
 *
 * The audit measured `outline: auto 1px rgb(16,16,16)` on every probed control
 * and **zero** `focus:` / `focus-visible:` utilities in all 48 `.tsx` files
 * (S2-11). That is worth saying precisely, because the two readings are very
 * different: focus was never *removed* — there is no `outline-none` anywhere —
 * it was simply never *styled*. So this is a deviation from reference §4
 * ("2 px `--focus` ring, offset 2 px, on every focusable element. Never
 * removed"), not the failure mode where a designer deletes the outline and
 * leaves nothing.
 *
 * Which is also why the rule below may say `outline-none` at all: it does so
 * only inside `focus-visible:`, and only in the same breath as a ring that is
 * stronger than what it replaces. A control that never reaches this rule keeps
 * the browser's outline, so the app can never end up with less than it has
 * today.
 *
 * `focus-visible` and not `focus`: a mouse click on a button should not leave a
 * ring behind it. Keyboard focus shows; pointer focus does not.
 *
 * It lives in one exported constant so no page has to remember it — reference
 * §7, last paragraph.
 */
export const FOCUS_RING =
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2';

/**
 * The same ring where there is no room for an offset — inside a table header
 * band, a toolbar, or a popover, where 2 px of offset would clip against a
 * neighbour.
 */
export const FOCUS_RING_TIGHT =
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus';

/**
 * The hit-target floor, reference §4: **never under 32 px**. Measured on
 * `/tickets` before this phase: 53 of 63 focusable elements were under it in a
 * dimension — the column funnels 16×16, the ticket screen's `Change` buttons
 * 16 px high, `Sign out` 64×16.
 *
 * It is a `min-` pair rather than a fixed size so a control that needs to be
 * bigger still can be, and a control that renders a 12 px icon still answers a
 * 32 px click.
 */
export const HIT_TARGET = 'min-h-[32px] min-w-[32px]';
